/**
 * Copyright 2025 AutoZone, Inc.
 * Content is confidential to and proprietary information of AutoZone, Inc., its
 * subsidiaries and affiliates.
 */

'use memo';
import {
  SortingTypeModel,
  type MerchShelfResponseModel,
  type GetMerchListResultsParamsModel,
} from '@/api/types/product-navigation-search-types';
import { useLocale } from '@/hooks/useLocale';
import { getAxios } from '@/lib/axios';
import { useAppState } from '@/stores/AppState';
import {
  type BaseInfiniteMergeFunction,
  type BaseMergedData,
  createInfiniteQuery,
} from '@/utils/query/createInfiniteQuery';
import { getCountryFromLocale } from '@/utils/getCountryFromLocale';
import { isAxiosError, type AxiosInstance } from 'axios';
import { useLegacyRouter } from '@/hooks/useLegacyRouter';
import { usePageType } from './getPageType';
import { parseUrl, stringifyUrl } from '@/utils/urlHelpers';
import { type Locale } from '@/types/i18n';
import { useHeaderData } from '@/features/header/api/getHeader';
import { getPreferredVehicle } from '@/features/header/utils/getPreferredVehicle';
import { pageTypes } from '@/constants/page';
import { showXMPreviewDate } from '@/utils/showXMPreviewDate';
import { useContentStackPLPData } from '@/features/productListingPage/api/getContentStackProductListingPageData';
import { extractVehicleQuestions } from '@/features/header/utils/getVehicleQuestions';
import { type MerchShelfResponse } from '../interface';
import { type QueryClient } from '@tanstack/react-query';
import { useAcesFlags } from '@/features/ymme/utils/acesFlags';
import { useQueryResultOnError } from '@/hooks/query/useQueryResultOnError';
import logger from '@/utils/logger';
import { requestBaseURLGCPExtOrInt } from '@/config/serviceAPI';
import { type BreadcrumbV2Model } from '@/api/types/browse-search-types';
import {
  getExperimentId,
  getSearchExperimentConfig,
  selectSearchExperimentUrl,
  buildSearchExperimentHeaders,
  buildSearchExperimentParams,
} from '@/utils/searchExperiment';
import { validateAndFilterVehicleParams } from '@/features/ymme/utils/validateVehicleParams';
import { useRetryConfigForCategory } from '@/components/RetryConfigContext';
import { useVertexPersonalizationParams } from '@/features/search/hooks/useVertexPersonalizationParams';
import { getSHA256 } from '@/utils/getSHA256';
import { isServer } from '@/utils/isServer';
import { useExperimentId } from '@/utils/searchExperiment/useExperimentId';
import { useRouter } from 'next/navigation';
import environmentConfig from '@/config/environment';
import { useDeviceType } from '@/utils/useDeviceType';
import { getBotEnabledFacetPath, getShelfRequestStoreId } from '@/utils/getShelfRequestStoreId';

const URL = '/sls/pd/product-navigation-search/v1/merch-shelves';
const ES_URL = '/sls/b2c/product-discovery-browse-search-data/v2/merch-shelves';

type QueryOptions = {
  locale: Locale;
  path?: string;
  canonicalPath?: string;
  botEnabledFacetPath?: string;
  recsPerPage?: number;
  makeModelYearPath?: string;
  storeId?: number;
  ignoreVehicleSpecificProductsCheck?: boolean;
  pageType?: string;
  preview?: boolean;
  tag?: string;
  year?: number;
  makeId?: number;
  modelId?: number;
  vehicleTypeId?: number;
  vehicleQuestions?: string;
  experimentId?: string;
  acesEnabled?: boolean;
  visitorId?: string;
  userId?: string;
};

const getMerchShelfResults = async (
  options: QueryOptions,
  pageNumber: number,
  axiosInstance?: AxiosInstance,
) => {
  const {
    locale,
    path,
    canonicalPath,
    botEnabledFacetPath,
    recsPerPage,
    makeModelYearPath,
    storeId,
    ignoreVehicleSpecificProductsCheck,
    pageType,
    preview,
    tag,
    year,
    makeId,
    modelId,
    vehicleTypeId,
    vehicleQuestions,
    experimentId,
    acesEnabled,
  } = options;
  const country = getCountryFromLocale(locale || 'en-US');
  const customerType = 'B2C';
  const salesChannel = 'ECOMM';

  const parsedUrl = parseUrl(path ?? '');
  const minPriceNumber = Number(parsedUrl.query.minPrice);
  const minPrice = !isNaN(minPriceNumber) ? minPriceNumber : undefined;
  const maxPriceNumber = Number(parsedUrl.query.maxPrice);
  const maxPrice = !isNaN(maxPriceNumber) ? maxPriceNumber : undefined;
  const queryRecsPerPage = Number(parsedUrl.query.recsPerPage);
  const partNumberSearch = parsedUrl.query.partNumberSearch;

  const sortValue = Object.values(SortingTypeModel).find(
    (sortOption) => sortOption === (parsedUrl.query.sort as SortingTypeModel),
  );

  const shouldAddIgnoreVehicleParam =
    pageType === pageTypes.ProductShelf &&
    (!!makeId || !!modelId || !!vehicleTypeId) &&
    ignoreVehicleSpecificProductsCheck === undefined;

  const ignoreVehicleSpecificProductsCheckOverride = shouldAddIgnoreVehicleParam
    ? false
    : ignoreVehicleSpecificProductsCheck;

  // Validate vehicle parameters - if any base param is missing, exclude ALL vehicle params
  const validatedVehicleParams = validateAndFilterVehicleParams({
    year,
    makeId,
    modelId,
    vehicleTypeId,
    vehicleQuestions,
  });

  const params: GetMerchListResultsParamsModel = {
    customerType,
    salesChannel,
    country,
    canonicalPath,
    facet: parsedUrl.query.facet ?? undefined,
    minPrice,
    maxPrice,
    botEnabledFacetPath,
    searchedKeyword: !parsedUrl.query.redirectUrl
      ? (parsedUrl.query.filterByKeyWord ?? parsedUrl.query.searchText)
      : undefined,
    partNumberSearch:
      typeof partNumberSearch === 'string' ? partNumberSearch.toLowerCase() === 'true' : undefined,
    pageNumber,
    recordsPerPage: recsPerPage
      ? recsPerPage
      : queryRecsPerPage && !isNaN(queryRecsPerPage)
        ? queryRecsPerPage
        : undefined,
    sort: sortValue,
    makeModelYearPath: makeModelYearPath === '' ? undefined : makeModelYearPath,
    storeId,
    ...(ignoreVehicleSpecificProductsCheckOverride !== undefined && {
      ignoreVehicleSpecificProductsCheck: ignoreVehicleSpecificProductsCheckOverride,
    }),
    preview,
    partGroupRules: tag ?? '',
    ...validatedVehicleParams,
  };

  const normalizedExperimentId = getExperimentId(experimentId, acesEnabled ?? false);
  const searchExpConfig = getSearchExperimentConfig({
    experimentId: normalizedExperimentId,
    experimentEnabled: acesEnabled ?? false,
  });

  const finalURL = selectSearchExperimentUrl(searchExpConfig, URL, ES_URL);
  // Hash userId if provided (for both client and server-side calls)
  const hashedUserId = options.userId ? await getSHA256(options.userId) : '';
  const enhancedParams = buildSearchExperimentParams(searchExpConfig, params, {
    visitorId: options.visitorId,
    userId: hashedUserId,
  });
  const headers = buildSearchExperimentHeaders(searchExpConfig);

  const response = await getAxios(axiosInstance).get<MerchShelfResponseModel>(
    `${requestBaseURLGCPExtOrInt}${finalURL}`,
    {
      ...(headers && { headers }),
      params: enhancedParams,
      withCredentials: !environmentConfig.DISABLE_GCP_API_CREDENTIALS,
    },
  );

  return merchShelfResultsSelector(response.data);
};

const merchShelfResultsSelector = ({
  attributionToken,
  filter,
  merchShelfResults,
  xfusionQueryId,
  redirectUrl,
}: MerchShelfResponseModel): MerchShelfResponse => {
  if (!merchShelfResults) {
    return {
      attributionToken,
      filter,
      merchShelfResults,
      xfusionQueryId,
      redirectUrl,
    };
  }

  // Map the breadcrumbs to maintain backward compatibility with old field names
  const mappedBreadCrumbs = merchShelfResults.breadCrumbs?.map((breadcrumb) => {
    return {
      ...breadcrumb,
      taxonomyId: breadcrumb.categoryIdPath,
      displayName: breadcrumb.categoryName,
      canonicalPath: breadcrumb.canonicalUrl,
    };
  });
  return {
    attributionToken,
    filter,
    merchShelfResults: {
      ...merchShelfResults,
      breadCrumbs: mappedBreadCrumbs as BreadcrumbV2Model[],
    },
    xfusionQueryId,
    redirectUrl,
  };
};

const selectInfiniteQueryData: BaseInfiniteMergeFunction<MerchShelfResponse, number> = (data) => {
  const mergedData = data?.pages?.reduce<MerchShelfResponse>(
    (acc, page) => {
      const { attributionToken, filter, xfusionQueryId, redirectUrl } = page;
      if (page?.merchShelfResults) {
        return {
          ...acc,
          merchShelfResults: {
            ...acc.merchShelfResults,
            ...page.merchShelfResults,
            skuRecords: [
              ...(acc.merchShelfResults?.skuRecords ?? []),
              ...(page.merchShelfResults?.skuRecords ?? []),
            ],
          },
          attributionToken,
          filter,
          xfusionQueryId,
          redirectUrl,
        };
      }
      return acc;
    },
    { merchShelfResults: { skuRecords: [] } } as unknown as MerchShelfResponse,
  );

  return {
    mergedData,
    pages: data.pages,
    pageParams: data.pageParams,
    pageNumber: data.pages.length,
  };
};

const {
  useData: useMerchShelfResultsData,
  query: merchShelfQuery,
  prefetch: prefetchMerchShelfResultsData,
  getFromCache,
} = createInfiniteQuery<
  MerchShelfResponse,
  QueryOptions,
  number,
  BaseMergedData<MerchShelfResponse, number>
>(['merchshelf-results'], getMerchShelfResults, {
  initialPageParam: 1,
  getNextPageParam: (lastPage, allPages) => {
    const totalRecords = lastPage?.merchShelfResults?.totalNumberOfRecords ?? 0;
    const currentPageNumber = allPages.length;

    // Count actual records loaded across all pages
    const loadedRecords = allPages.reduce((sum, page) => {
      return sum + (page.merchShelfResults?.skuRecords?.length ?? 0);
    }, 0);

    // Only fetch next page if we haven't loaded all records yet
    if (loadedRecords < totalRecords) {
      return currentPageNumber + 1;
    }
    return undefined;
  },
  selectInfiniteQueryData,
});

export const useMerchShelfResults = ({
  enabled = true,
  ...rest
}: {
  enabled?: boolean;
} = {}) => {
  const appState = useAppState();
  const legacyRouter = useLegacyRouter();
  const router = useRouter();
  const locale = useLocale();
  const isBot = useDeviceType() === 'bot';
  const experimentId = useExperimentId();
  const { data: pageTypeData } = usePageType();
  const { data: headerData } = useHeaderData();
  const preferredVehicle = getPreferredVehicle(headerData);
  const { data: plpCMSData, isSuccess: isPLPCMSDataSuccess } = useContentStackPLPData({
    enabled: true,
  });
  const isMerchShelfPage = legacyRouter.route === '/deals/[cmsPath]/[[...slug]]';
  const { acesEnabled } = useAcesFlags();
  const vertexParams = useVertexPersonalizationParams();
  const retryConfig = useRetryConfigForCategory('SEARCH');

  const reqUrl = isServer ? appState.state.reqUrl : legacyRouter.asPath;
  if (reqUrl == null) {
    throw new Error(`A 'path' option is required`);
  }
  const parsedReqUrl = parseUrl(reqUrl);
  const taxonomyPath = pageTypeData?.taxonomyPath || undefined;
  const botEnabledFacetPath = isMerchShelfPage
    ? getBotEnabledFacetPath(reqUrl)
    : pageTypeData?.botEnabledFacetPath || undefined;
  delete parsedReqUrl.query.loadMore;
  const cleanedReqUrl = stringifyUrl(parsedReqUrl);
  const path = legacyRouter.route === '/cms/taxonomy-selector' ? taxonomyPath : cleanedReqUrl;
  const storeNumber = Number(headerData?.storeNumber);
  const lwTag = plpCMSData?.additional_attributes_merch_shelf_page?.lucidworks_fusion_tag
    ? plpCMSData.additional_attributes_merch_shelf_page.lucidworks_fusion_tag
    : undefined;
  const vehicleQuestions = extractVehicleQuestions(
    headerData?.vehicleMap?.acesVehicleAttributes?.vehicleQuestionsIds,
    true,
  ) as string;

  // Validate vehicle parameters - if any base param is missing, exclude ALL vehicle params
  const validatedVehicleParams = validateAndFilterVehicleParams({
    year: preferredVehicle?.year,
    makeId: preferredVehicle?.makeId,
    modelId: preferredVehicle?.modelId,
    vehicleTypeId: preferredVehicle?.vehicleTypeId,
    vehicleQuestions: vehicleQuestions || undefined,
  });

  // Normalize experimentId for cache key consistency
  const normalizedExperimentId = getExperimentId(experimentId, acesEnabled ?? false);

  const queryOptions: QueryOptions = {
    locale,
    path,
    canonicalPath: pageTypeData?.canonicalPath || parsedReqUrl.url,
    botEnabledFacetPath,
    recsPerPage: 24,
    makeModelYearPath: pageTypeData?.makeModelYearPath,
    storeId: getShelfRequestStoreId(
      !isNaN(storeNumber) ? storeNumber : undefined,
      isBot,
      botEnabledFacetPath,
    ),
    ignoreVehicleSpecificProductsCheck:
      typeof parsedReqUrl.query.ignoreVehicleSpecificProductsCheck === 'string'
        ? parsedReqUrl.query.ignoreVehicleSpecificProductsCheck === 'true'
        : undefined,
    pageType: pageTypeData?.pageType || undefined,
    preview: showXMPreviewDate(),
    tag: lwTag,
    ...validatedVehicleParams,
    acesEnabled,
    ...(acesEnabled && normalizedExperimentId && { experimentId: normalizedExperimentId }),
    ...(vertexParams.shouldInjectParams && {
      visitorId: vertexParams.visitorId,
      userId: vertexParams.userId,
    }),
    ...rest,
  };

  const infiniteQueryResult = useMerchShelfResultsData({
    queryOptions,
    enabled:
      enabled &&
      vertexParams.isMyProfileReady &&
      isMerchShelfPage &&
      isPLPCMSDataSuccess &&
      !!lwTag &&
      acesEnabled,
    staleTime: 30 * 60000,
    ...retryConfig,
    retryOnMount: false,
  });

  useQueryResultOnError(infiniteQueryResult, (err) => {
    logger.error({
      message: 'Error fetching merch shelf results',
      meta: {
        error: isAxiosError<unknown>(err) ? err.response?.data : err,
      },
    });

    try {
      router.replace('/errorPage');
    } catch {
      throw new Error(`There was a problem redirecting to /errorPage`);
    }
  });

  const {
    data: infiniteData,
    fetchNextPage,
    hasNextPage,
    fetchPreviousPage,
    hasPreviousPage,
    isFetching,
    isPending,
    status,
    refetch,
    isFetchingNextPage,
  } = infiniteQueryResult;

  const { mergedData, pages, pageNumber = 1 } = infiniteData ?? {};

  return {
    pages,
    data: mergedData,
    pageNumber,
    fetchNextPage,
    hasNextPage,
    fetchPreviousPage,
    hasPreviousPage,
    isFetching,
    isPending,
    status,
    refetch,
    isFetchingNextPage,
  };
};

export const prefetchMerchShelfResultsInfinite = async (
  axiosInstance: AxiosInstance,
  queryClient: QueryClient,
  options: QueryOptions,
) => {
  return prefetchMerchShelfResultsData(queryClient, options, axiosInstance);
};

export const getMerchShelfResultsFromCache = (queryClient: QueryClient, options: QueryOptions) => {
  return getFromCache(queryClient, options);
};

export { merchShelfQuery };
